import { useState } from 'react'

import { Button, Notice, OctagonAlertIcon, Tooltip } from '@buffer-mono/popcorn'

import { getChannelName } from '~publish/helpers/channels'
import { useRefreshChannelConnection } from '~publish/hooks/useRefreshChannelConnection'
import type { RefreshableChannel } from '~publish/pages/Community/components/ChannelRefresh/ChannelRefreshCard'
import { ReconnectChannelsDialog } from '~publish/pages/Community/components/ChannelRefresh/ReconnectChannelsDialog'
import { FeedRow } from '~publish/pages/Community/components/FeedRow'
import { canPerform } from '~publish/utils/permissions/canPerform'

import type { Channel } from '../CommunityConfig'

import type { JSX } from 'react'

function getApiVersion(channel: Channel): string | null | undefined {
  if (!('apiVersion' in channel)) return undefined

  return channel.apiVersion === null || typeof channel.apiVersion === 'string'
    ? channel.apiVersion
    : undefined
}

function toRefreshableChannels(channels: Channel[]): RefreshableChannel[] {
  return channels.flatMap((channel) => {
    if (!channel.id) return []

    const name =
      ('name' in channel ? channel.name : undefined) ??
      ('displayName' in channel ? channel.displayName : undefined)
    const avatar = 'avatar' in channel ? channel.avatar : undefined
    const isLocked = 'isLocked' in channel ? channel.isLocked : undefined

    return [
      {
        id: channel.id,
        name: name ?? '',
        avatar: typeof avatar === 'string' ? avatar : null,
        service: channel.service,
        apiVersion: getApiVersion(channel),
        isLocked: isLocked === true,
      },
    ]
  })
}

type ChannelRefreshNoticeProps = {
  channelsNeedingRefresh: Channel[]
}

// A separate component so the dialog's open state resets whenever the list
// drops to one channel and the single-channel notice takes over.
function MultipleChannelsRefreshNotice({
  channelsNeedingRefresh,
}: ChannelRefreshNoticeProps): JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  return (
    <FeedRow>
      <Notice variant="error" icon={<OctagonAlertIcon />}>
        <Notice.Heading>
          We&apos;re having trouble connecting to some of your channels
        </Notice.Heading>
        <Notice.Text>
          Refresh the connections to start replying to comments.
        </Notice.Text>
        <Notice.Actions>
          <Button
            variant="critical"
            size="small"
            onClick={(): void => setIsDialogOpen(true)}
          >
            Refresh Channels
          </Button>
        </Notice.Actions>
      </Notice>
      <ReconnectChannelsDialog
        channels={toRefreshableChannels(channelsNeedingRefresh)}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </FeedRow>
  )
}

export function ChannelRefreshNotice({
  channelsNeedingRefresh,
}: ChannelRefreshNoticeProps): JSX.Element {
  const refreshChannelConnection = useRefreshChannelConnection()

  if (channelsNeedingRefresh.length === 1) {
    const channel = channelsNeedingRefresh[0]
    const channelName =
      ('name' in channel ? channel.name : undefined) ??
      ('displayName' in channel ? channel.displayName : undefined)
    const serviceLabel = getChannelName(channel.service)
    // Per-channel reconnect authority — mirrors removeChannel's gate today
    // (org-admin) but evaluated against this specific channel's allowedActions.
    const canReconnect = canPerform(channel, 'reconnectChannel')

    const handleRefresh = (): void => {
      if (!canReconnect) return
      refreshChannelConnection({
        channel: {
          id: channel.id ?? '',
          service: channel.service,
          name: channelName ?? '',
          apiVersion: getApiVersion(channel),
        },
        cta: 'publish-comments-all-channels-notice-refresh',
      })
    }

    return (
      <FeedRow>
        <Notice variant="error" icon={<OctagonAlertIcon />}>
          <Notice.Heading>
            We&apos;re having trouble connecting to your {channelName}{' '}
            {serviceLabel} channel
          </Notice.Heading>
          <Notice.Text>
            Refresh the connection to start replying to comments.
          </Notice.Text>
          <Notice.Actions>
            {canReconnect ? (
              <Button variant="critical" size="small" onClick={handleRefresh}>
                Refresh Connection
              </Button>
            ) : (
              <Tooltip content="You don't have permission to refresh the connection. Please contact the administrator of your organization.">
                {/* span wrapper so the Tooltip receives pointer events even
                    when the Button is `disabled` (native disabled buttons
                    don't fire hover/focus events). */}
                <span>
                  <Button variant="critical" size="small" disabled>
                    Refresh Connection
                  </Button>
                </span>
              </Tooltip>
            )}
          </Notice.Actions>
        </Notice>
      </FeedRow>
    )
  }

  return (
    <MultipleChannelsRefreshNotice
      channelsNeedingRefresh={channelsNeedingRefresh}
    />
  )
}
